/*
 * Copyright (c) 2019. ProVision Media Group Ltd. <https://provision.bg>
 * Venelin Iliev <http://veneliniliev.com>
 */

import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { PaginatedResponse } from '@app/shared/models/paginated-response.model';
import { UsersService } from '@app/settings/users/users.service';
import { ApiQuery } from '@app/core/http/api-query';
import { Logger } from '@app/core';
import { User } from '@app/settings/users/user.model';
import { UsersFormService } from '@app/settings/users/users-form.service';
import { LaravelModelsEnum } from '@app/shared/models/model.model';
import { KendoGridComponent } from '@app/shared/kendo/kendo-grid.component';
import { KendoGridService } from '@app/shared/kendo/kendo-grid.service';
import { MediaLibraryAcceptTypes } from '@app/shared/components/media-library/media-library-accept-types.enum';
import { environment } from '@env/environment';
import { ContactTypes } from '@app/shared/components/contact/contact-types.enum';
import { DataSet } from '@app/shared/kendo/data-set';
import { CustomKendoFilter } from '@app/shared/kendo/custom-kendo-filter';
import { UserRoleType } from '@app/settings/users/user-role-type.enum';
import { AccessService } from '@app/core/access/access.service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

const log: Logger = new Logger('localesListComponent');

@UntilDestroy()
@Component({
  selector: 'app-users-list',
  templateUrl: './users-list.component.html',
  styleUrls: ['./users-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsersListComponent extends KendoGridComponent implements OnInit {
  usersResponse: PaginatedResponse;
  data: GridDataResult;
  state: DataStateChangeEvent;
  apiQuery: ApiQuery = new ApiQuery();
  LaravelModels = LaravelModelsEnum;
  ContactTypes = ContactTypes;
  MediaLibraryAcceptTypes = MediaLibraryAcceptTypes;
  env = environment;
  UserRoleType = UserRoleType;

  constructor(
    private usersService: UsersService,
    private usersFormService: UsersFormService,
    protected kendoGridService: KendoGridService,
    public accessService: AccessService,
    private cdr: ChangeDetectorRef
  ) {
    super(kendoGridService);
  }

  ngOnInit() {
    this.state = new DataSet().addSort('employee', 'desc').addSort('active', 'desc');

    const storageFilter = CustomKendoFilter.getStateFromStorage('UsersListComponent');
    if (storageFilter) {
      this.state = storageFilter;
    }
    this.loadData(this.state);
  }

  /**
   * Load data from API
   */
  loadData(state: DataStateChangeEvent = null) {
    this.state = state;
    this.apiQuery.setDataSetFilters(state).addIncludes('roles', 'avatar', 'contacts', 'unread_notifications_count');

    this.usersService
      .index(this.apiQuery)
      .pipe(untilDestroyed(this))
      .subscribe((response: PaginatedResponse) => {
        this.usersResponse = response;
        this.data = response.getGridData();
        this.cdr.markForCheck();
      });

    CustomKendoFilter.pushStateInStorage('UsersListComponent', this.state);
  }

  add() {
    this.usersFormService.open().then(
      () => {
        this.loadData(this.state);
      },
      () => {
        log.info('modal closed');
      }
    );
  }

  /**
   *
   * @param user User model
   */
  edit(user: User) {
    this.usersFormService.open(user).then(
      () => {
        this.loadData(this.state);
      },
      () => {}
    );
  }

  /**
   *
   * @param user User object
   */
  delete(user: User) {
    this.usersService
      .destroy(user.id)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.loadData(this.state);
      });
  }

  export() {
    this.usersService.export(this.apiQuery);
  }

  clearFilters() {
    this.apiQuery = new ApiQuery();
    this.state = new DataSet().addSort('employee', 'desc').addSort('active', 'desc');
    this.loadData(this.state);
  }
}
